"use client";

import { ProspectStatus } from "@prisma/client";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { setClientProspectStatus } from "@/app/actions/prospect-search/prospects/action";
import { ModalStore } from "@/app/stores/ModalStore";
import { PageParams } from "@/app/types/common/DefaultProps";
import { ClientProspectsPage } from "@/app/types/prospect/ClientProspects";
import { ProspectingStats } from "@/app/types/prospect/ProspectingStats";
import { ULogger } from "@/app/utils/ULogger";
import { Globals } from "@/app/vars/Globals";
import { QueryKey } from "@/app/vars/QueryKeys";

function applyStatsDelta(queryClient: QueryClient, next: ProspectStatus, previous: ProspectStatus) {
  queryClient.setQueriesData<ProspectingStats>(
    { queryKey: [QueryKey.PROSPECTING_STATS] },
    (old) => {
      if (!old) {
        return old;
      }

      const found = (status: ProspectStatus) => (status === ProspectStatus.QUALIFIED ? 1 : 0);
      const rejected = (status: ProspectStatus) => (status === ProspectStatus.REJECTED ? 1 : 0);

      return {
        ...old,
        linkedinFoundCount: Math.max(0, old.linkedinFoundCount + found(next) - found(previous)),
        linkedinRejectedCount: Math.max(
          0,
          old.linkedinRejectedCount + rejected(next) - rejected(previous),
        ),
      };
    },
  );
}

// Move the row's status in the cached lists on the click, so the tag flips without waiting
// for the round-trip. The reconcile refetch that follows removes it from a filter it no
// longer belongs to.
function applyListStatus(queryClient: QueryClient, prospectId: number, next: ProspectStatus) {
  queryClient.setQueriesData<ClientProspectsPage>(
    { queryKey: [QueryKey.CLIENT_PROSPECTS] },
    (old) =>
      old
        ? {
            ...old,
            prospects: old.prospects.map((prospect) =>
              prospect.id === prospectId ? { ...prospect, status: next } : prospect,
            ),
          }
        : old,
  );
}

function reconcile(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_PROSPECTS] });
  queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_PROSPECT_DETAIL] });
  queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECTING_STATS] });
  queryClient.invalidateQueries({ queryKey: [QueryKey.USER_IMPORTED_PROFILES] });
  queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_WORKSPACE] });
  queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_DASHBOARD] });
}

// A verdict on a prospect also writes the match it came from, and the LinkedIn card
// counts matches, so the stats and the imported list go stale with the lists. The
// delta moves the tiles on the click; the invalidation is what makes them exact.
export function settleProspectStatus(
  queryClient: QueryClient,
  next: ProspectStatus,
  previous?: ProspectStatus,
) {
  if (previous && previous !== next) {
    applyStatsDelta(queryClient, next, previous);
  }

  reconcile(queryClient);
}

export function useProspectStatusActions(params: PageParams) {
  const queryClient = useQueryClient();

  // Under the fit bar, qualifying is a call against the score, so it asks for the same
  // justification a rejection does. Above it, the score already says why.
  const qualify = async (prospectId: number, previous?: ProspectStatus, scoreFit?: number) => {
    if (scoreFit !== undefined && scoreFit < Globals.PROSPECT_MIN_FIT_SCORE) {
      ModalStore.getState().openProspectStatusReason(
        params,
        prospectId,
        ProspectStatus.QUALIFIED,
        () => settleProspectStatus(queryClient, ProspectStatus.QUALIFIED, previous),
      );
      return;
    }

    // Optimistic: the row and the tiles move on the click, the round-trip only confirms.
    applyListStatus(queryClient, prospectId, ProspectStatus.QUALIFIED);
    if (previous && previous !== ProspectStatus.QUALIFIED) {
      applyStatsDelta(queryClient, ProspectStatus.QUALIFIED, previous);
    }

    try {
      await setClientProspectStatus(prospectId, ProspectStatus.QUALIFIED);
    } catch (error) {
      ULogger.error("Failed to qualify prospect", error);
    } finally {
      reconcile(queryClient);
    }
  };

  const reject = (prospectId: number, previous?: ProspectStatus) => {
    ModalStore.getState().openProspectStatusReason(
      params,
      prospectId,
      ProspectStatus.REJECTED,
      () => settleProspectStatus(queryClient, ProspectStatus.REJECTED, previous),
    );
  };

  return { qualify, reject };
}
